import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import { SECTION_ICON_NAMES } from './WaadooSection';

/**
 * The custom fields of the `odoo-project` form (platform-catalog fork,
 * scaffolder/odoo-project/template.yaml), a module of the `scaffolder`
 * plugin like `projectNamePickerModule`: each `FormFieldBlueprint` attaches
 * to the scaffolder's form fields, and its component is loaded lazily with
 * them.
 *
 * Fields only: custom layouts (`ui:ObjectFieldTemplate`) are not wired by the
 * new frontend system's scaffolder page (plugin-scaffolder 1.39,
 * `TemplatesSubPage` calls `useCustomLayouts(undefined)`).
 */
const waadooSectionField = FormFieldBlueprint.make({
  name: 'waadoo-section',
  params: {
    field: async () => {
      const { WaadooSection } = await import('./WaadooSection');
      return createFormField({
        name: 'WaadooSection',
        component: WaadooSection,
        schema: makeFieldSchema({
          output: z => z.null(),
          uiOptions: z =>
            z.object({
              icon: z.enum(SECTION_ICON_NAMES).optional(),
              details: z.string().optional(),
            }),
        }),
      });
    },
  },
});

const odooAppsPickerField = FormFieldBlueprint.make({
  name: 'odoo-apps-picker',
  params: {
    field: async () => {
      const { OdooAppsPicker } = await import('./OdooAppsPicker');
      return createFormField({
        name: 'OdooAppsPicker',
        component: OdooAppsPicker,
        schema: makeFieldSchema({
          output: z => z.array(z.string()),
          uiOptions: z =>
            z.object({
              columns: z.number().int().min(1).max(4).optional(),
              groups: z
                .array(
                  z.object({ title: z.string(), items: z.array(z.string()) }),
                )
                .optional(),
              collapsibleGroups: z.boolean().optional(),
            }),
        }),
      });
    },
  },
});

const capacityProfilePickerField = FormFieldBlueprint.make({
  name: 'capacity-profile-picker',
  params: {
    field: async () => {
      const { CapacityProfilePicker, capacityProfilePickerValidation } =
        await import('./CapacityProfilePicker');
      return createFormField({
        name: 'CapacityProfilePicker',
        component: CapacityProfilePicker,
        validation: capacityProfilePickerValidation,
        schema: makeFieldSchema({
          output: z =>
            z.object({
              profile: z.string().optional(),
              odoo: z.record(z.number()).optional(),
              database: z.record(z.number()).optional(),
            }),
          uiOptions: z =>
            z.object({
              node: z
                .object({ cpu: z.number(), memory: z.number() })
                .optional(),
            }),
        }),
      });
    },
  },
});

const haOptionsPickerField = FormFieldBlueprint.make({
  name: 'ha-options-picker',
  params: {
    field: async () => {
      const { HaOptionsPicker, haOptionsPickerValidation } = await import(
        './HaOptionsPicker'
      );
      return createFormField({
        name: 'HaOptionsPicker',
        component: HaOptionsPicker,
        validation: haOptionsPickerValidation,
        schema: makeFieldSchema({
          output: z =>
            z.object({
              cache: z.boolean().optional(),
              // `cache` in a template not yet migrated (odoo/valkeyRenaming.ts).
              redis: z.boolean().optional(),
              autoscaling: z
                .object({
                  enabled: z.boolean().optional(),
                  minReplicas: z.number().optional(),
                  maxReplicas: z.number().optional(),
                  targetCPU: z.number().optional(),
                  scaleDownWindow: z.number().optional(),
                })
                .optional(),
              database: z
                .object({
                  mode: z.string().optional(),
                  syncReplication: z.boolean().optional(),
                })
                .optional(),
            }),
          uiOptions: z =>
            z.object({
              node: z
                .object({ cpu: z.number(), memory: z.number() })
                .optional(),
            }),
        }),
      });
    },
  },
});

const branchesPickerField = FormFieldBlueprint.make({
  name: 'branches-picker',
  params: {
    field: async () => {
      const { BranchesPicker } = await import('./BranchesPicker');
      return createFormField({
        name: 'BranchesPicker',
        component: BranchesPicker,
        schema: makeFieldSchema({
          output: z => z.array(z.string()),
          uiOptions: z => z.object({ baseDomain: z.string() }),
        }),
      });
    },
  },
});

const odooProjectSummaryField = FormFieldBlueprint.make({
  name: 'odoo-project-summary',
  params: {
    field: async () => {
      const { OdooProjectSummary } = await import('./OdooProjectSummary');
      return createFormField({
        name: 'OdooProjectSummary',
        component: OdooProjectSummary,
        schema: makeFieldSchema({
          output: z => z.null(),
          uiOptions: z => z.object({ baseDomain: z.string() }),
        }),
      });
    },
  },
});

export default createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    waadooSectionField,
    odooAppsPickerField,
    capacityProfilePickerField,
    haOptionsPickerField,
    branchesPickerField,
    odooProjectSummaryField,
  ],
});
